import React, { useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHeading, PillButton } from "@/components/primitives";
import { Icon } from "@/lib/icons";
import { useSeo } from "@/lib/useSeo";
import { COMPANY, ENQUIRY_TYPES, CAPITAL_TREE } from "@/lib/content";
import { BUSINESS_HOURS, CONTACT_FAQ, CONTACT_CARDS } from "@/lib/contentV2";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const initial = {
  full_name: "", company_name: "", email: "", phone: "",
  enquiry_type: "", subject: "", message: "", consent: false, website: "",
};

export default function Contact() {
  useSeo({
    title: "Contact Us",
    description:
      "Get in touch with Vardhix Technologies Private Limited for business partnerships, technology solutions, platform enquiries, media, careers and vendor enquiries. Based in Pune, Maharashtra.",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }],
  });

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.enquiry_type || !form.subject || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (!form.consent) {
      toast.error("Please provide consent to submit the form.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Thank you! Your enquiry has been received.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        overline="Contact Us"
        title="Let&rsquo;s talk technology"
        sub="Whether it&rsquo;s a partnership, a technology solution, or a platform enquiry — our team is ready to help."
      />

      {/* Contact cards */}
      <section className="relative py-12">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid sm:grid-cols-3 gap-5">
          {CONTACT_CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06}>
              <div className="glass rounded-2xl p-6 h-full hover:border-teal/40 transition-colors" data-testid={`contact-card-${i}`}>
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                  <Icon name={c.icon} className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-heading font-semibold text-white">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                <p className="mt-3 text-sm text-teal">{c.value}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative py-16 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
          {/* Info */}
          <div className="lg:col-span-4 space-y-5">
            <Reveal>
              <div className="glass rounded-2xl p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-teal">Registered Office</p>
                <p className="mt-2 text-slate-300 text-sm">{COMPANY.registeredOffice}</p>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="glass rounded-2xl p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-teal">Corporate Office</p>
                <p className="mt-2 text-slate-300 text-sm">{COMPANY.corporateOffice}</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="glass rounded-2xl p-6 space-y-3">
                <div className="flex items-center gap-3 text-sm text-slate-300"><Icon name="Mail" className="h-4 w-4 text-teal" /> {COMPANY.email}</div>
                <div className="flex items-center gap-3 text-sm text-slate-300"><Icon name="ExternalLink" className="h-4 w-4 text-teal" /> {COMPANY.website}</div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="glass rounded-2xl p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-teal">Business Hours</p>
                <div className="mt-3 space-y-2">
                  {BUSINESS_HOURS.map((b) => (
                    <div key={b.day} className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">{b.day}</span>
                      <span className="text-slate-200">{b.hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="glass rounded-2xl p-6 border-l-2 border-teal/50">
                <p className="text-sm text-slate-300">For account-related support, please contact Capital Tree Funding through its official support channels.</p>
                <a href={CAPITAL_TREE.url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-teal hover:text-teal-glow" data-testid="contact-ctf-support">
                  <Icon name="ExternalLink" className="h-4 w-4" /> Go to {CAPITAL_TREE.display}
                </a>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <Reveal>
              <form onSubmit={onSubmit} className="glass rounded-3xl p-8 space-y-5" data-testid="contact-form">
                <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full Name *"><Input data-testid="contact-name" value={form.full_name} onChange={set("full_name")} placeholder="Your name" className="bg-white/5 border-white/10" /></Field>
                  <Field label="Company Name"><Input data-testid="contact-company" value={form.company_name} onChange={set("company_name")} placeholder="Company" className="bg-white/5 border-white/10" /></Field>
                  <Field label="Email *"><Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-white/5 border-white/10" /></Field>
                  <Field label="Phone Number"><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+91" className="bg-white/5 border-white/10" /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Enquiry Type *">
                    <Select value={form.enquiry_type} onValueChange={set("enquiry_type")}>
                      <SelectTrigger className="bg-white/5 border-white/10" data-testid="contact-enquiry-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent className="bg-matte border-white/10 text-white">
                        {ENQUIRY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Subject *"><Input data-testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="Subject" className="bg-white/5 border-white/10" /></Field>
                </div>
                <Field label="Message *"><Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="How can we help?" rows={5} className="bg-white/5 border-white/10" /></Field>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.consent} onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))} data-testid="contact-consent" className="mt-0.5 border-white/30 data-[state=checked]:bg-teal data-[state=checked]:border-teal" />
                  <span className="text-sm text-slate-400">I consent to Vardhix Technologies storing and processing my details to respond to this enquiry.</span>
                </label>
                <PillButton type="submit" testid="contact-submit" icon="ArrowRight">
                  {loading ? "Sending..." : "Send Enquiry"}
                </PillButton>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-16 border-t border-white/10">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <SectionHeading center overline="FAQ" title="Frequently asked questions" />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10" data-testid="contact-faq">
              {CONTACT_FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="glass rounded-2xl px-6 mb-3 border-white/10">
                  <AccordionTrigger className="text-left text-white font-heading hover:no-underline" data-testid={`faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="relative py-16 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading overline="Location" title="Find us in Pune" sub="806, Gera Imperium Rise, Hinjewadi Phase 2, Pune, Maharashtra, India." />
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-3xl overflow-hidden border border-white/10">
              <iframe
                title="Vardhix Technologies Location"
                src="https://www.google.com/maps?q=Gera+Imperium+Rise,+Hinjewadi+Phase+2,+Pune,+Maharashtra,+India&output=embed"
                width="100%"
                height="420"
                style={{ border: 0, filter: "grayscale(0.4) contrast(1.05) brightness(0.85)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300 text-sm">{label}</Label>
      {children}
    </div>
  );
}
